import type { AnswerMap, LeadPayload, QuizDefinition, QuizResult } from "@quiz/quiz-core";
import { getAnalysisVisual, getQuestionOrdinal, getQuestionTotal, getQuizPresentation } from "@quiz/quiz-definitions";
import { emitQuizEvent, type AnalyticsPayload } from "./analytics/events";
import { trackMetaLead } from "./analytics/tracking";
import { resolveCurrentQuiz } from "./router/quizRouter";
import { renderAnalysisScreen } from "./screens/AnalysisScreen";
import { renderCustomStartScreen } from "./screens/CustomStartScreen";
import { renderLeadScreen } from "./screens/LeadScreen";
import { renderNotFoundScreen } from "./screens/NotFoundScreen";
import { renderQuestionScreen } from "./screens/QuestionScreen";
import { renderResultScreen } from "./screens/ResultScreen";
import { renderNativeStartScreen } from "./screens/StartScreen";
import { renderSuccessScreen } from "./screens/SuccessScreen";
import { QuizSession } from "./state/quizSession";
import { getUiCopy, type ResolvedUiCopy } from "./ui/copy";
import { clear, setCssVar } from "./utils";
import type { LeadResponse } from "./api/leadApi";
import { renderFlowHeader } from "./components/FlowHeader";
import { preloadCampaignMedia } from "./components/CampaignMedia";
import { comicQuestionAssetsReady, prepareComicQuestionAssets } from "./components/ComicQuestionAssets";
import { prepareAmbientAudio, startAmbientAudio } from "./audio/ambientAudio";
import { buildSelfbookingUrl } from "./booking/selfbooking";

type View = "start" | "question" | "analysis" | "result" | "lead" | "success";

export class QuizApp {
  private quiz?: QuizDefinition;
  private session?: QuizSession;
  private copy?: ResolvedUiCopy;
  private leadResponse?: LeadResponse;
  private view: View = "start";
  private questionEnterAnimation = false;
  private questionTransitioning = false;
  private activeQuestionId?: string;
  private questionShownAt = 0;
  private analysisStartedAt = 0;
  private emittedOnce = new Set<string>();

  constructor(private readonly root: HTMLElement) {}

  start(): void {
    const route = resolveCurrentQuiz();
    if (!route.quiz) {
      this.root.replaceChildren(renderNotFoundScreen(route.slug));
      return;
    }
    this.quiz = route.quiz;
    document.title = route.quiz.metadata.pageTitle ?? route.quiz.metadata.name;
    this.root.dataset.quiz = route.quiz.slug;
    this.root.dataset.presentation = getQuizPresentation(route.quiz).style ?? "default";
    this.root.dataset.flowLayout = getQuizPresentation(route.quiz).flowLayout ?? "default";
    const parchment = getQuizPresentation(route.quiz).comicQuest?.parchmentSrc;
    if (parchment) this.root.style.setProperty("--comic-parchment-image", `url("${parchment}")`);
    this.session = new QuizSession(route.quiz);
    this.copy = getUiCopy(route.quiz);
    applyTheme(route.quiz);
    prepareAmbientAudio(getQuizPresentation(route.quiz).ambientAudio, (eventName) => {
      if (this.quiz && this.session) emitQuizEvent(eventName, baseMeta(this.quiz, this.session));
    });
    emitQuizEvent("quiz_view", baseMeta(route.quiz, this.session));

    const sessionRoute = this.session.route();
    if (!route.quiz.start.enabled || route.quiz.start.renderer === "none") this.view = "question";
    if (Object.keys(this.session.answers).length > 0 && !sessionRoute.isComplete) this.view = "question";
    if (sessionRoute.isComplete) this.view = "result";
    this.render();
  }

  private render(): void {
    if (!this.quiz || !this.session || !this.copy) return;
    const pendingQuestion = this.view === "question" ? this.session.currentQuestion() : undefined;
    if (getQuizPresentation(this.quiz).comicQuest && pendingQuestion && !comicQuestionAssetsReady(this.quiz, pendingQuestion)) {
      this.questionTransitioning = true;
      this.root.setAttribute("aria-busy", "true");
      void prepareComicQuestionAssets(this.quiz, pendingQuestion).then(() => {
        this.questionTransitioning = false;
        this.render();
      });
      return;
    }
    this.root.removeAttribute("aria-busy");
    if (getQuizPresentation(this.quiz).comicQuest) window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    clear(this.root);
    if (this.view === "start") {
      const firstQuestion = this.session.currentQuestion();
      const startOptions = {
        quiz: this.quiz,
        copy: this.copy,
        firstQuestion,
        selectedAnswerId: firstQuestion ? this.session.answers[firstQuestion.id] : undefined,
        progress: this.session.progress(),
        onStart: () => {
          if (this.questionTransitioning) return;
          this.session!.markStarted();
          void startAmbientAudio();
          emitQuizEvent("quiz_start", baseMeta(this.quiz!, this.session));
          this.view = "question" as View;
          this.render();
        },
        onAnswer: (answerId: string, answerButton: HTMLButtonElement) => {
          if (!firstQuestion) return;
          this.handleAnswer(firstQuestion.id, answerId, answerButton, 420);
        }
      };
      const renderer = this.quiz.start.renderer ?? "native";
      this.root.append(renderer === "custom-html" ? renderCustomStartScreen(startOptions) : renderNativeStartScreen(startOptions));
      return;
    }

    if (getQuizPresentation(this.quiz).flowLayout === "compact") {
      this.root.append(renderFlowHeader(this.quiz));
    }

    if (this.view === "question") {
      const comic = getQuizPresentation(this.quiz).comicQuest;
      preloadCampaignMedia(
        getAnalysisVisual(this.quiz),
        this.quiz.resultPresentation?.visual,
        comic?.analysisBackdrop,
        comic?.lockedResultArt,
        comic?.leadArt,
        comic?.unlockedResultArt
      );
      const question = this.session.currentQuestion();
      if (!question) {
        this.view = shouldSkipAnalysis(this.quiz) ? "result" : "analysis";
        this.render();
        return;
      }
      const questionIndex = this.quiz.questions.findIndex((candidate) => candidate.id === question.id);
      preloadCampaignMedia(this.quiz.questions[questionIndex + 1]?.visual);
      if (comic && this.quiz.questions[questionIndex + 1]) void prepareComicQuestionAssets(this.quiz, this.quiz.questions[questionIndex + 1]);
      this.activeQuestionId = question.id;
      this.questionShownAt = performance.now();
      emitQuizEvent("quiz_progress", {
        ...baseMeta(this.quiz, this.session),
        question_id: question.id,
        question_index: getQuestionOrdinal(this.quiz, this.session.route().visitedQuestionIds, question.id),
        progress_percent: this.session.progress()
      });
      this.root.append(
        renderQuestionScreen({
          quiz: this.quiz,
          question,
          answers: this.session.answers,
          selectedAnswerId: this.session.answers[question.id],
          progress: this.session.progress(),
          questionIndex: getQuestionOrdinal(this.quiz, this.session.route().visitedQuestionIds, question.id),
          totalSteps: getQuestionTotal(this.quiz),
          canGoBack: this.session.route().visitedQuestionIds.indexOf(question.id) > 0,
          enterAnimation: this.consumeQuestionEnterAnimation(),
          copy: this.copy,
          onBack: () => this.handleBack(),
          onAnswer: (answerId, answerButton) => this.handleAnswer(question.id, answerId, answerButton, 320)
        })
      );
      return;
    }

    if (this.view === "analysis") {
      if (shouldSkipAnalysis(this.quiz)) {
        this.view = "result";
        this.render();
        return;
      }
      this.analysisStartedAt = performance.now();
      this.emitOnce("analysis_start", "analysis_start", baseMeta(this.quiz, this.session));
      this.root.append(
        renderAnalysisScreen(this.quiz, () => {
          emitQuizEvent("analysis_complete", {
            ...baseMeta(this.quiz!, this.session),
            duration_ms: Math.round(performance.now() - this.analysisStartedAt)
          });
          this.view = "result";
          this.render();
        })
      );
      return;
    }

    if (this.view === "result") {
      const result: QuizResult = this.leadResponse?.authoritative_result ?? this.session.result();
      this.emitOnce(`result_view:${this.leadResponse ? "unlocked" : "locked"}`, this.leadResponse ? "result_unlocked_view" : "result_view", {
        ...baseMeta(this.quiz, this.session),
        result_id: result.id,
        result_mode: resultMode(result),
        result_score: result.score,
        result_state: this.leadResponse ? "unlocked" : "locked",
        ...correctnessSummary(this.quiz, this.session.answers)
      });
      if (this.quiz.leadForm.placement === "inline-result" && !this.leadResponse) {
        this.emitOnce("lead_form_view", "lead_form_view", baseMeta(this.quiz, this.session));
      }
      this.root.append(
        renderResultScreen({
          quiz: this.quiz,
          result,
          answers: this.session.answers,
          copy: this.copy,
          leadResponse: this.leadResponse,
          onLead: () => {
            emitQuizEvent("result_unlock_click", { ...baseMeta(this.quiz!, this.session), cta_id: "open_result" });
            this.view = "lead";
            this.render();
          },
          leadFormOptions: {
            quiz: this.quiz,
            copy: this.copy,
            createPayload: (contact, consent) => this.session!.toLeadPayload(contact, consent),
            onStart: (fieldName) => this.emitOnce("lead_form_start", "lead_form_start", { ...baseMeta(this.quiz!, this.session), field_name: fieldName }),
            onValidationError: (fieldName, errorCode) => emitQuizEvent("lead_validation_error", { ...baseMeta(this.quiz!, this.session), field_name: fieldName, error_code: errorCode }),
            onSubmit: (payload) => emitLeadSubmit(this.quiz!, this.session!, payload),
            onSuccess: (response, payload) => {
              emitLeadSuccess(this.quiz!, this.session!, response);
              this.leadResponse = response;
              if (this.openBookingIfConfigured(payload, response)) return;
              this.view = shouldUseSuccessAfterInlineLead(this.quiz!) ? "success" : "result";
              this.render();
            },
            onError: () => {
              emitQuizEvent("lead_error", { ...baseMeta(this.quiz!, this.session), error_code: "submit_failed" });
            }
          }
        })
      );
      return;
    }

    if (this.view === "lead") {
      this.emitOnce("lead_form_view", "lead_form_view", baseMeta(this.quiz, this.session));
      this.root.append(
        renderLeadScreen({
          quiz: this.quiz,
          copy: this.copy,
          createPayload: (contact, consent) => this.session!.toLeadPayload(contact, consent),
          onStart: (fieldName) => this.emitOnce("lead_form_start", "lead_form_start", { ...baseMeta(this.quiz!, this.session), field_name: fieldName }),
          onValidationError: (fieldName, errorCode) => emitQuizEvent("lead_validation_error", { ...baseMeta(this.quiz!, this.session), field_name: fieldName, error_code: errorCode }),
          onSubmit: (payload) => emitLeadSubmit(this.quiz!, this.session!, payload),
          onSuccess: (response, payload) => {
            emitLeadSuccess(this.quiz!, this.session!, response);
            this.leadResponse = response;
            if (this.openBookingIfConfigured(payload, response)) return;
            this.view = "success";
            this.render();
          },
          onError: () => {
            emitQuizEvent("lead_error", { ...baseMeta(this.quiz!, this.session), error_code: "submit_failed" });
          }
        })
      );
      return;
    }

    if (this.view === "success" && this.leadResponse) {
      const result: QuizResult = this.leadResponse.authoritative_result ?? this.session.result();
      this.emitOnce("result_unlocked_view", "result_unlocked_view", {
        ...baseMeta(this.quiz, this.session),
        result_id: result.id,
        result_mode: resultMode(result),
        result_score: result.score,
        result_state: "unlocked",
        ...correctnessSummary(this.quiz, this.session.answers)
      });
      this.emitOnce("personal_plan_view", "personal_plan_view", { ...baseMeta(this.quiz, this.session), cta_id: "personal_plan" });
      this.root.append(
        renderSuccessScreen(
          this.quiz,
          result,
          this.leadResponse,
          this.copy,
          () => {
            this.session!.reset();
            this.emittedOnce.clear();
            this.activeQuestionId = undefined;
            this.leadResponse = undefined;
            this.view = "start";
            emitQuizEvent("quiz_view", baseMeta(this.quiz!, this.session));
            this.render();
          },
          () => {
            if (!this.leadResponse) return;
            emitQuizEvent("telegram_plan_cta_click", {
              ...baseMeta(this.quiz!, this.session),
              event_id: this.leadResponse.event_id,
              cta_id: "telegram_personal_plan"
            });
          },
          this.session.answers
        )
      );
    }
  }

  private handleBack(): void {
    if (!this.quiz || !this.session || this.questionTransitioning) return;
    const current = this.session.currentQuestion();
    if (getQuizPresentation(this.quiz).comicQuest && current?.id === this.quiz.startQuestionId) {
      emitQuizEvent("quiz_back", { ...baseMeta(this.quiz, this.session), question_id: current.id, next_question_id: "cover", question_index: 1 });
      this.view = "start";
      this.render();
      return;
    }
    const goBack = () => {
      const before = this.session!.displayRoute();
      const currentId = this.session!.currentQuestion()?.id;
      this.session!.back();
      emitQuizEvent("quiz_back", {
        ...baseMeta(this.quiz!, this.session),
        question_id: currentId,
        next_question_id: this.session!.currentQuestion()?.id,
        question_index: currentId ? before.visitedQuestionIds.indexOf(currentId) + 1 : undefined
      });
      this.view = getQuizPresentation(this.quiz!).entryComposition === "offer-cards" && this.session!.currentQuestion()?.id === this.quiz!.startQuestionId
        ? "start" : "question";
      this.render();
    };
    const visited = this.session.displayRoute().visitedQuestionIds;
    const previousId = visited[Math.max(0, visited.indexOf(current?.id ?? "") - 1)];
    this.transitionQuestion(goBack, this.quiz.questions.find((question) => question.id === previousId));
  }

  private handleAnswer(questionId: string, answerId: string, answerButton: HTMLButtonElement, delayMs: number): void {
    if (!this.quiz || !this.session || this.questionTransitioning) return;
    if (this.view === "start" && questionId === this.quiz.startQuestionId) {
      this.session.markStarted();
      this.emitOnce("quiz_start", "quiz_start", baseMeta(this.quiz, this.session));
    }
    for (const node of Array.from(answerButton.parentElement?.children ?? [])) node.classList.remove("is-selected");
    answerButton.classList.add("is-selected");
    const beforeRoute = this.session.route();
    const questionIndex = beforeRoute.visitedQuestionIds.indexOf(questionId) + 1;
    const route = this.session.answer(questionId, answerId);
    const progress = this.session.progress();
    const selectedQuestion = this.quiz.questions.find((question) => question.id === questionId);
    const selectedAnswer = selectedQuestion?.answers.find((answer) => answer.id === answerId);
    const hasCorrectAnswer = selectedQuestion?.answers.some((answer) => answer.tags?.includes("correct")) ?? false;
    emitQuizEvent("quiz_answer", {
      ...baseMeta(this.quiz, this.session),
      question_id: questionId,
      answer_id: answerId,
      progress_percent: progress,
      question_index: questionIndex,
      is_correct: hasCorrectAnswer ? selectedAnswer?.tags?.includes("correct") === true : undefined,
      duration_ms: this.activeQuestionId === questionId ? Math.round(performance.now() - this.questionShownAt) : undefined
    });
    emitQuizEvent("quiz_branch", {
      ...baseMeta(this.quiz, this.session),
      question_id: questionId,
      answer_id: answerId,
      next_question_id: route.currentQuestionId
    });
    const finish = () => {
      if (route.isComplete) {
        emitQuizEvent("quiz_complete", {
          ...baseMeta(this.quiz!, this.session),
          duration_ms: this.session!.elapsedMs(),
          ...correctnessSummary(this.quiz!, this.session!.answers)
        });
        this.view = shouldSkipAnalysis(this.quiz!) ? "result" : "analysis";
      } else {
        this.view = "question";
      }
      this.render();
    };

    if (!route.isComplete && hasQuestionTransition(this.quiz) && this.view === "question") {
      if (getQuizPresentation(this.quiz).comicQuest) this.questionTransitioning = true;
      const selectedPause = prefersReducedMotion() ? 0 : 80;
      window.setTimeout(() => this.transitionQuestion(finish), selectedPause);
      return;
    }

    window.setTimeout(finish, delayMs);
  }

  private transitionQuestion(afterExit: () => void, targetQuestion = this.session?.currentQuestion()): void {
    if (!this.quiz || !hasQuestionTransition(this.quiz)) {
      afterExit();
      return;
    }
    if (getQuizPresentation(this.quiz).comicQuest && targetQuestion && !comicQuestionAssetsReady(this.quiz, targetQuestion)) {
      this.questionTransitioning = true;
      this.root.setAttribute("aria-busy", "true");
      void prepareComicQuestionAssets(this.quiz, targetQuestion).then(() => this.transitionQuestion(afterExit, targetQuestion));
      return;
    }
    this.root.removeAttribute("aria-busy");
    if (getQuizPresentation(this.quiz).questionTransition === "instant") {
      // Replace only complete, decoded frames; never fade through the dark backdrop.
      this.questionEnterAnimation = false;
      afterExit();
      this.questionTransitioning = false;
      return;
    }
    const shell = this.root.querySelector<HTMLElement>(".question-shell, .comic-question");
    if (!shell || prefersReducedMotion()) {
      this.questionEnterAnimation = true;
      afterExit();
      this.questionTransitioning = false;
      return;
    }
    this.questionTransitioning = true;
    shell.classList.add("question-shell--exiting");
    window.setTimeout(() => {
      this.questionEnterAnimation = true;
      afterExit();
      this.questionTransitioning = false;
    }, 150);
  }

  private consumeQuestionEnterAnimation(): boolean {
    const value = this.questionEnterAnimation;
    this.questionEnterAnimation = false;
    return value;
  }

  private emitOnce(key: string, event: Parameters<typeof emitQuizEvent>[0], payload: AnalyticsPayload): void {
    if (this.emittedOnce.has(key)) return;
    this.emittedOnce.add(key);
    emitQuizEvent(event, payload);
  }

  private openBookingIfConfigured(payload: LeadPayload, response: LeadResponse): boolean {
    if (!this.quiz || !this.session) return false;
    const booking = getQuizPresentation(this.quiz).booking;
    if (!booking) return false;
    const url = buildSelfbookingUrl(booking, payload);
    emitQuizEvent("selfbooking_open", {
      ...baseMeta(this.quiz, this.session),
      event_id: response.event_id,
      cta_id: "book_free_lesson"
    });
    window.setTimeout(() => window.location.assign(url), 120);
    return true;
  }
}

function applyTheme(quiz: QuizDefinition): void {
  setCssVar("--color-primary", quiz.theme.primary);
  setCssVar("--color-accent", quiz.theme.accent);
  setCssVar("--color-bg", quiz.theme.background);
  setCssVar("--color-card", quiz.theme.card);
  setCssVar("--color-text", quiz.theme.text);
  setCssVar("--color-muted", quiz.theme.mutedText);
  setCssVar("--color-button-text", quiz.theme.buttonText);
  setCssVar("--color-progress", quiz.theme.progress);
  setCssVar("--color-border", quiz.theme.border);
  setCssVar("--radius", quiz.theme.radius);
  setCssVar("--font-family", quiz.theme.fontFamily);
  setCssVar("--headline-scale", quiz.theme.headlineScale);
  setCssVar("--body-scale", quiz.theme.bodyScale);
  setCssVar("--answer-weight", quiz.theme.answerWeight ?? 560);
  setCssVar("--space-scale", quiz.theme.spacingDensity === "compact" ? 0.86 : quiz.theme.spacingDensity === "spacious" ? 1.16 : 1);
}

function baseMeta(quiz: QuizDefinition, session?: QuizSession): AnalyticsPayload {
  const attribution = session?.attribution;
  return {
    quiz_id: quiz.id,
    quiz_slug: quiz.slug,
    quiz_version: quiz.version,
    ...(session ? { quiz_session_id: session.id } : {}),
    utm_source: attribution?.utm_source,
    utm_medium: attribution?.utm_medium,
    utm_campaign: attribution?.utm_campaign,
    utm_content: attribution?.utm_content,
    utm_term: attribution?.utm_term,
    landing_path: safePath(attribution?.landing_url),
    referrer_host: safeHost(attribution?.referrer)
  };
}

function emitLeadSubmit(quiz: QuizDefinition, session: QuizSession, payload: LeadPayload): void {
  emitQuizEvent("lead_submit", {
    ...baseMeta(quiz, session),
    event_id: payload.event_id
  });
}

function emitLeadSuccess(quiz: QuizDefinition, session: QuizSession, response: LeadResponse): void {
  emitQuizEvent("lead_success", {
    ...baseMeta(quiz, session),
    event_id: response.event_id,
    result_id: response.authoritative_result.id,
    result_mode: resultMode(response.authoritative_result)
  });
  if (!response.duplicate) trackMetaLead(response.event_id, quiz.slug);
}

function shouldSkipAnalysis(quiz: QuizDefinition): boolean {
  return getQuizPresentation(quiz).skipAnalysis === true;
}

function shouldUseSuccessAfterInlineLead(quiz: QuizDefinition): boolean {
  return getQuizPresentation(quiz).completion?.mode === "telegram";
}

function hasQuestionTransition(quiz: QuizDefinition): boolean {
  return getQuizPresentation(quiz).questionTransition !== undefined;
}

function prefersReducedMotion(): boolean {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function resultMode(result: QuizResult): string {
  return "mode" in result && result.mode === "answer-observations" ? "answer-observations" : "scored";
}

function correctnessSummary(quiz: QuizDefinition, answers: AnswerMap): AnalyticsPayload {
  const graded = quiz.questions.filter((question) => question.answers.some((answer) => answer.tags?.includes("correct")));
  if (graded.length === 0) return {};
  const correct = graded.filter((question) => question.answers.find((answer) => answer.id === answers[question.id])?.tags?.includes("correct")).length;
  return { correct_count: correct, mistakes_count: graded.length - correct };
}

function safePath(value?: string): string | undefined {
  if (!value) return undefined;
  try { return new URL(value).pathname; } catch { return undefined; }
}

function safeHost(value?: string): string | undefined {
  if (!value) return undefined;
  try { return new URL(value).hostname; } catch { return undefined; }
}
